// Web-Push-Client-Schicht. Solange kein ÖFFENTLICHER VAPID-Key
// konfiguriert ist, wird KEINE (Fake-)Subscription erzeugt und KEIN
// echter Push vorgetäuscht. Der eigentliche Versand liegt serverseitig
// (register_merchant_web_push_subscription + Worker), aber Registrierung
// ist ohne VAPID nicht möglich.
import { getSupabase } from "../lib/supabase";
import { HAS_VAPID, VAPID_PUBLIC_KEY } from "../lib/env";

export type PushStatus =
  | "not_supported"
  | "not_configured"
  | "permission_denied"
  | "permission_default"
  | "subscription_active"
  | "subscription_error";

export type NotificationPermissionState = "granted" | "denied" | "default" | "unsupported";

export function notificationPermission(): NotificationPermissionState {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission as NotificationPermissionState;
}

/** Nur nach Nutzeraktion aufrufen; bei "default" höchstens einmal fragen. */
export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission !== "default") return Notification.permission as NotificationPermissionState;
  try {
    return (await Notification.requestPermission()) as NotificationPermissionState;
  } catch {
    return "denied";
  }
}

export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && typeof Notification !== "undefined";
}

/** Aktuellen Web-Push-Status ermitteln (ohne etwas zu erzeugen). */
export function currentPushStatus(): PushStatus {
  if (!pushSupported()) return "not_supported";
  if (!HAS_VAPID) return "not_configured";
  const perm = notificationPermission();
  if (perm === "denied") return "permission_denied";
  if (perm === "default") return "permission_default";
  return "subscription_active";
}

/**
 * Registriert eine echte Web-Push-Subscription — NUR wenn VAPID konfiguriert
 * und Permission granted ist. Ohne VAPID passiert bewusst nichts.
 */
export async function ensureWebPushSubscription(merchantId: string): Promise<PushStatus> {
  if (!pushSupported()) return "not_supported";
  if (!HAS_VAPID) return "not_configured";
  const perm = notificationPermission();
  if (perm === "denied") return "permission_denied";
  if (perm === "default") return "permission_default";
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as BufferSource,
    });
    const json = sub.toJSON();
    const { error } = await getSupabase().rpc("register_merchant_web_push_subscription", {
      p_merchant_id: merchantId,
      p_endpoint: sub.endpoint,
      p_p256dh: json.keys?.p256dh ?? "",
      p_auth: json.keys?.auth ?? "",
    });
    if (error) return "subscription_error";
    return "subscription_active";
  } catch {
    return "subscription_error";
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
